import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import {
  getProviderIntegrationProcesses,
  initiateGetProviderMetadata,
  getConfigMetadataByProcessGuid,
} from './getProviderMetadata';

dayjs.extend(utc);

export interface PollingConfig {
  maxRetries?: number;
  retryIntervalMs?: number;
}

export interface PollingResult<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  retryCount: number;
}

/**
 * Generic utility for polling provider metadata with async processing
 * Can be used by any ACS provider that supports the async metadata fetching pattern
 *
 * @example
 * ```typescript
 * // For a simple one-off polling operation:
 * const result = await pollProviderMetadata(
 *   'SOME_PROVIDER_1_0',
 *   channelId,
 *   settings,
 *   { maxRetries: 300, retryIntervalMs: 3000 }
 * );
 *
 * if (result.success) {
 *   console.log('Data:', result.data);
 * } else {
 *   console.error('Error:', result.error);
 * }
 * ```
 *
 * @example
 * ```typescript
 * // For a provider setup function:
 * async function setupSomeProviderFields() {
 *   setMetadataLoading(true);
 *
 *   try {
 *     if (usePollingForThisProvider) {
 *       const result = await pollProviderMetadata(
 *         'SOME_PROVIDER_1_0',
 *         convertToUUID(channel._id),
 *         settings
 *       );
 *
 *       if (!result.success) {
 *         setMetadataError(result.error);
 *         return;
 *       }
 *
 *       data = result.data;
 *     } else {
 *       data = await getProviderMetadata(...);
 *     }
 *
 *     // Process the data...
 *     setupDropdownValidators(...);
 *
 *   } catch (error) {
 *     setMetadataError(error.message);
 *   } finally {
 *     setMetadataLoading(false);
 *   }
 * }
 * ```
 */
export class ProviderMetadataPoller {
  private readonly defaultConfig: Required<PollingConfig> = {
    maxRetries: 720, // 1 hour with 5-second intervals
    retryIntervalMs: 5000,
  };

  private config: Required<PollingConfig>;

  constructor(config: PollingConfig = {}) {
    this.config = { ...this.defaultConfig, ...config };
  }

  /**
   * Polls a provider integration process until completion or failure
   * @private
   */
  private async pollIntegrationProcess(
    channelId: string,
    provider: string,
    processGuid: string
  ): Promise<{ isStopPolling: boolean; error?: string }> {
    try {
      const processIntegrationData = await getProviderIntegrationProcesses(
        channelId,
        provider,
        processGuid
      );

      if (processIntegrationData?.length > 0) {
        const process = processIntegrationData[0];

        // Check if process failed
        if (
          process?.failedAt &&
          dayjs.utc(process.failedAt).format('YYYY-MM-DD') !== '0001-01-01'
        ) {
          return {
            isStopPolling: true,
            error:
              'Process failed: The integration process encountered an issue and could not complete successfully',
          };
        }

        // Check if process succeeded
        if (
          process?.succeededAt &&
          dayjs.utc(process.succeededAt).format('YYYY-MM-DD') !== '0001-01-01'
        ) {
          return {
            isStopPolling: true,
            error: undefined,
          };
        }

        // Process is still running
        return { isStopPolling: false };
      }

      return {
        isStopPolling: true,
        error: 'No metadata found for processGuid',
      };
    } catch (err: any) {
      return {
        isStopPolling: true,
        error:
          err?.message || 'No metadata returned from access control provider',
      };
    }
  }

  /**
   * Starts polling for a specific process and returns the result when complete
   * @private
   */
  private async startPolling(
    channelId: string,
    provider: string,
    processGuid: string
  ): Promise<PollingResult> {
    const isPolling = true;
    let retryCount = 0;

    while (isPolling && retryCount < this.config.maxRetries!) {
      const integrationProcess = await this.pollIntegrationProcess(
        channelId,
        provider,
        processGuid
      );

      if (integrationProcess.isStopPolling) {
        if (integrationProcess.error) {
          return {
            success: false,
            error: integrationProcess.error,
            retryCount,
          };
        }

        // Process succeeded, now fetch the actual data
        try {
          const data = await getConfigMetadataByProcessGuid(
            provider,
            channelId,
            processGuid
          );

          return {
            success: true,
            data,
            retryCount,
          };
        } catch (error: any) {
          return {
            success: false,
            error:
              error?.message ||
              'Failed to fetch metadata after process completion',
            retryCount,
          };
        }
      }

      retryCount++;
      await new Promise(resolve =>
        setTimeout(resolve, this.config.retryIntervalMs)
      );
    }

    // Polling reached maximum retries
    return {
      success: false,
      error: 'Polling reached maximum retry limit',
      retryCount,
    };
  }

  /**
   * Initiates async metadata fetching and polls until completion
   * This is the main public method that providers should use
   *
   * @param provider - The provider identifier (e.g., 'GENETEC_5_9')
   * @param channelId - The channel UUID
   * @param settings - Provider-specific settings object
   * @param enableTenantMapping - Whether tenant mapping is enabled
   * @returns Promise resolving to the polling result with data or error
   */
  async fetchMetadataWithPolling<T = any>(
    provider: string,
    channelId: string,
    settings: Record<string, any>,
    enableTenantMapping: boolean = false
  ): Promise<PollingResult<T>> {
    try {
      // Initiate the async process
      const processGuidData = await initiateGetProviderMetadata(
        provider,
        channelId,
        settings,
        enableTenantMapping
      );

      if (!processGuidData?.processGuid) {
        return {
          success: false,
          error: 'Unable to get processGuid',
          retryCount: 0,
        };
      }

      // Start polling for completion
      const result = await this.startPolling(
        channelId,
        provider,
        processGuidData.processGuid
      );

      // Log retry count for monitoring
      console.info(
        `Provider ${provider} polling completed with ${result.retryCount} retries`
      );

      return result;
    } catch (error: any) {
      return {
        success: false,
        error: error?.message || 'Failed to initiate metadata fetching',
        retryCount: 0,
      };
    }
  }
}

/**
 * Default instance with standard configuration
 */
export const defaultProviderMetadataPoller = new ProviderMetadataPoller();

/**
 * Convenience function for one-off polling operations
 *
 * @param provider - The provider identifier (e.g., 'GENETEC_5_9')
 * @param channelId - The channel UUID
 * @param settings - Provider-specific settings object
 * @param config - Optional polling configuration (maxRetries, retryIntervalMs)
 * @param enableTenantMapping - Whether tenant mapping is enabled
 * @returns Promise resolving to the polling result
 */
export async function pollProviderMetadata<T = any>(
  provider: string,
  channelId: string,
  settings: Record<string, any>,
  config?: PollingConfig,
  enableTenantMapping: boolean = false
): Promise<PollingResult<T>> {
  // Always create a fresh instance unless you pass an existing one via DI.
  const poller = new ProviderMetadataPoller(config);

  return poller.fetchMetadataWithPolling<T>(
    provider,
    channelId,
    settings,
    enableTenantMapping
  );
}

/**
 * Higher-order function to create a provider setup function with async polling support
 * This makes it easy for any provider to add polling functionality to their existing setup
 *
 * @param provider - The provider identifier
 * @param fallbackFn - The original synchronous metadata fetching function
 * @param config - Optional polling configuration
 * @returns A setup function that can handle both sync and async operations
 */
export function createAsyncProviderSetup<T = any>(
  provider: string,
  fallbackFn: (
    provider: string,
    channelId: string,
    settings: Record<string, any>
  ) => Promise<T>,
  config?: PollingConfig
) {
  return async (
    channelId: string,
    settings: Record<string, any>,
    usePolling: boolean = false
  ): Promise<PollingResult<T> & { data: T }> => {
    if (usePolling) {
      const result = await pollProviderMetadata<T>(
        provider,
        channelId,
        settings,
        config
      );

      return result as PollingResult<T> & { data: T };
    }

    try {
      const data = await fallbackFn(provider, channelId, settings);

      return {
        success: true,
        data,
        retryCount: 0,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error?.message || 'Failed to fetch metadata',
        retryCount: 0,
        data: undefined as any,
      };
    }
  };
}
